import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { UserCheck, Mail, Phone, ArrowRightCircle } from 'lucide-react';
import { guestBooking } from '../utils/api';

/**
 * GuestsPanel — admin view of the guest_clients table (roadmap item
 * #20 Phase 2).
 *
 * Lists every guest who has booked, shows their pending vs already-
 * promoted state, and provides a one-click "Promote to client" action
 * that creates a real users row, re-binds their appointments, and
 * sends them a "set your password" email.
 *
 * Mounted inline in ClientManagement.js so admins find it where they
 * already manage clients. Renders nothing when there are no guests
 * (zero-cost when the feature isn't being used).
 */

const GuestsPanel = () => {
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [promotingId, setPromotingId] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await guestBooking.listGuests();
      setGuests(res.data || []);
    } catch (e) {
      // Silent fail — most admins won't have any guests, and the
      // endpoint just 404s on installs that haven't run migration 045.
      console.warn('Load guests:', e);
    } finally {
      setLoading(false);
    }
  };

  const handlePromote = async (guest) => {
    if (!guest.email) {
      return toast.error("This guest has no email on file. Edit the record to add one before promoting.");
    }
    if (!window.confirm(`Promote ${guest.first_name} ${guest.last_name} to a full client account? They'll be emailed a link to set their password, and all their bookings will move under the new account.`)) return;
    setPromotingId(guest.id);
    try {
      const res = await guestBooking.promote(guest.id);
      toast.success(`Promoted! Reset link emailed to ${res.data.email}.`);
      // Surface the link too in case email failed silently.
      if (res.data.reset_link) {
        try { await navigator.clipboard.writeText(res.data.reset_link); toast.info('Reset link also copied to clipboard'); } catch (_) {}
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Promote failed');
    } finally {
      setPromotingId(null);
    }
  };

  // Keep the admin's existing client list view uncluttered when the
  // feature isn't in use. Empty + loaded === render nothing.
  if (loading || guests.length === 0) return null;

  return (
    <div className="card mb-4 border-l-4 border-purple-500">
      <div className="flex items-center space-x-3 mb-3">
        <UserCheck className="text-purple-600" size={22} />
        <h2 className="text-xl font-bold">Guests <span className="text-sm font-normal text-gray-500">({guests.length})</span></h2>
      </div>
      <p className="text-sm text-gray-600 mb-3">
        These contacts booked without creating an account. Promote any of them
        to a full client account to give them online access and consolidate
        their booking history.
      </p>
      <div className="divide-y divide-gray-200">
        {guests.map((g) => {
          const promoted = !!g.promoted_user_id;
          return (
            <div key={g.id} className="py-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-gray-900">{g.first_name} {g.last_name}</span>
                  {promoted ? (
                    <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">Promoted</span>
                  ) : (
                    <span className="text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full">Guest</span>
                  )}
                  <span className="text-xs text-gray-500">{g.appointment_count} appt{Number(g.appointment_count) === 1 ? '' : 's'}</span>
                </div>
                <div className="text-sm text-gray-600 flex flex-wrap gap-x-4 mt-1">
                  {g.email && <span className="inline-flex items-center gap-1"><Mail size={12} /> {g.email}</span>}
                  {g.phone && <span className="inline-flex items-center gap-1"><Phone size={12} /> {g.phone}</span>}
                  {g.pet_name && <span>Pet: {g.pet_name}{g.pet_size ? ` (${g.pet_size})` : ''}</span>}
                </div>
              </div>
              {!promoted && (
                <button
                  onClick={() => handlePromote(g)}
                  disabled={promotingId === g.id || !g.email}
                  className="btn btn-secondary flex items-center gap-2 disabled:opacity-50"
                  title={!g.email ? 'Email required to promote' : 'Promote to full client account'}
                >
                  <ArrowRightCircle size={16} />
                  {promotingId === g.id ? 'Promoting…' : 'Promote'}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default GuestsPanel;
